import { useCallback, useEffect, useRef, useState } from 'react';
import { getMailNotify } from '../utils/api';
import logger from '../utils/logger';
import {
  describeError,
  isOperationFailed,
  isOperationRunning,
  needsReload,
  normalizeState,
  operationErrorCode,
  STALE_VERSION_TEXT,
} from './mailNotifyStatus';

/**
 * 202 之後向後端輪詢的節奏（SA 情境 L）：每 3 秒 GET 一次（GET 本身會推進 APISIX 未完成的操作），最多 30 秒。
 * 30 秒還沒結果就停，表單與按鈕恢復可按，狀態卡副標題換成「連線逾時，請稍後手動重新整理。」——
 * 這時再送出會被 APISIX 以 409 擋下，不會重複建立。
 */
const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 30 * 1000;

const sortRules = (rules) =>
  [...(Array.isArray(rules) ? rules : [])].sort((a, b) => (a.sortOrder ?? 99) - (b.sortOrder ?? 99));

const isPendingData = (data) => data?.state === 'PENDING' || isOperationRunning(data?.operation);

/**
 * 信件即時通知新架構的資料來源（SA v2.0 §5.1）。狀態卡、規則區、新增表單共用這一份。
 *
 * 狀態與規則是同一支 `GET /web/integrations/microsoft/mail-notify`，所以兩者同時出現、同時更新。
 * 每次寫入都帶最後一次讀到的 `rulesVersion`（If-Match）：別處（MyAgent、另一個分頁、對話）先改過，
 * 後端回 409 VERSION_CONFLICT，這裡重讀，畫面顯示最新內容，⛔ 不自動重送（使用者要先看到最新內容，情境 K）。
 *
 * @returns {{
 *   data: null|{state:string, reason:?string, rulesVersion:*, operation:?Object, maxRules:?number, rules:Array, notify:?Object},
 *   syncing: boolean, loadError: boolean, loadErrorDetail: ?Object, busy: boolean, pollTimedOut: boolean, reportedErrorCode: ?string,
 *   refresh: () => Promise<void>,
 *   clearReportedError: () => void,
 *   write: (request: (rulesVersion) => Promise<{pending:boolean, data:*}>, options?: {fallback?: string, onPending?: Function})
 *     => Promise<{ok: boolean, data?: *, timedOut?: boolean, error?: {message: string, errorCode: ?string}, reloaded?: ?Object}>
 * }}
 */
export function useMailNotify() {
  const [data, setData] = useState(null);
  // 同步中：剛進頁面、設定視窗關閉、App 切回前景時重抓；這段期間狀態卡顯示「同步中」、規則區收起（C07）
  const [syncing, setSyncing] = useState(true);
  const [loadError, setLoadError] = useState(false);
  // 讀取失敗的原因（code／errorCode），狀態卡依此決定說明行文字（FUNC-037）
  const [loadErrorDetail, setLoadErrorDetail] = useState(null);
  // 剛收到 202、還沒輪詢到結果；與 data 的 PENDING 分開記，因為 202 之後第一次 GET 之前畫面上還是舊狀態
  const [awaiting, setAwaiting] = useState(false);
  const [pollTimedOut, setPollTimedOut] = useState(false);
  // 這個頁面最近一次寫入已在動作現場（規則區、表單、狀態卡按鈕）報過的錯誤碼；狀態卡的「上一次操作失敗」遇到同一碼就不再重複
  const [reportedErrorCode, setReportedErrorCode] = useState(null);
  const dataRef = useRef(null);
  const fetchSeqRef = useRef(0);
  const pollStartRef = useRef(null);
  // 等 202 結果的寫入動作；輪詢有結果（或逾時）時一起放行
  const waitersRef = useRef([]);

  const settle = useCallback((result) => {
    waitersRef.current.splice(0).forEach((resolve) => resolve(result));
  }, []);

  /**
   * @param {{ visible?: boolean, quiet?: boolean }} options
   *   visible：顯示「同步中」（使用者感受得到的重抓）；quiet：失敗不把整頁切成讀取失敗（寫入後與輪詢時的重讀）
   */
  const fetchStatus = useCallback(async ({ visible = false, quiet = false } = {}) => {
    const seq = ++fetchSeqRef.current;
    if (visible) {
      setSyncing(true);
      setPollTimedOut(false);
    }
    try {
      const next = await getMailNotify();
      const state = normalizeState(next?.state);
      if (!state) {
        logger.warn('信件即時通知', '狀態 API 回傳未知的 state，當作讀取失敗', { state: next?.state });
        throw new Error('unknown state');
      }
      // ⛔ 晚到的回應不可蓋掉較新的：例如寫入後的重讀與輪詢同時在途
      if (seq !== fetchSeqRef.current) return dataRef.current;
      const normalized = { ...next, state, rules: sortRules(next.rules) };
      dataRef.current = normalized;
      setData(normalized);
      setLoadError(false);
      setLoadErrorDetail(null);
      return normalized;
    } catch (e) {
      if (seq === fetchSeqRef.current && !quiet) {
        setLoadError(true);
        setLoadErrorDetail(e);
      }
      throw e;
    } finally {
      if (seq === fetchSeqRef.current && visible) setSyncing(false);
    }
  }, []);

  const refresh = useCallback(() => fetchStatus({ visible: true }).catch(() => {}), [fetchStatus]);
  // 背景同步（10-20，每分鐘一次）：不顯示「同步中」，失敗不切成讀取失敗；成功時順便清掉之前的讀取失敗
  const sync = useCallback(() => fetchStatus({ quiet: true }).catch(() => {}), [fetchStatus]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // 卸載時把還在等的寫入放掉，避免 promise 永遠不結束
  useEffect(() => () => settle({ ok: false, timedOut: true }), [settle]);

  const serverPending = isPendingData(data);
  const shouldPoll = (awaiting || serverPending) && !pollTimedOut && !loadError;

  /*
   * 處理中就每 3 秒問一次。用 setTimeout 串接：上一次還沒回來就不送下一次。
   * 依賴只有 shouldPoll：輪詢回來仍在處理時不重啟、30 秒不歸零；重新進入處理中（新的 202、切回前景重抓）才從頭計時。
   * 頁面在背景時跳過這一輪（沒人看；切回前景會整個重抓）。
   */
  useEffect(() => {
    if (!shouldPoll) return undefined;
    let stopped = false;
    let timer = null;
    if (pollStartRef.current === null) pollStartRef.current = Date.now();

    const schedule = () => {
      if (!stopped) timer = setTimeout(tick, POLL_INTERVAL_MS);
    };
    async function tick() {
      if (stopped) return;
      if (Date.now() - pollStartRef.current >= POLL_TIMEOUT_MS) {
        pollStartRef.current = null;
        setAwaiting(false);
        setPollTimedOut(true);
        settle({ ok: false, timedOut: true });
        return;
      }
      if (document.visibilityState !== 'visible') {
        schedule();
        return;
      }
      try {
        const next = await fetchStatus({ quiet: true });
        if (stopped) return;
        if (!isPendingData(next)) {
          pollStartRef.current = null;
          setAwaiting(false);
          if (isOperationFailed(next?.operation)) {
            settle({ ok: false, error: { errorCode: operationErrorCode(next.operation) } });
          } else {
            settle({ ok: true });
          }
          return;
        }
      } catch {
        // 單次查不到不代表背景停了，照樣繼續問，逾時再說
      }
      schedule();
    }

    schedule();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [shouldPoll, fetchStatus, settle]);

  // 動作現場帶著錯誤關掉（刪除確認框、新增表單按「取消」）時呼叫：錯誤跟著消失，改由狀態卡接手顯示，不會兩邊都看不到
  const clearReportedError = useCallback(() => setReportedErrorCode(null), []);

  const write = useCallback(
    async (request, { fallback = '操作失敗，請稍後再試。', onPending } = {}) => {
      setPollTimedOut(false);
      setReportedErrorCode(null);
      const fail = (error) => {
        const described = describeError(error, fallback);
        setReportedErrorCode(described.errorCode);
        return { ok: false, error: described };
      };
      try {
        const result = await request(dataRef.current?.rulesVersion ?? null);
        if (result?.pending) {
          if (onPending) onPending();
          const waiting = new Promise((resolve) => waitersRef.current.push(resolve));
          pollStartRef.current = Date.now();
          setAwaiting(true);
          const outcome = await waiting;
          if (!outcome.ok && outcome.error) return fail(outcome.error);
          return outcome;
        }
        // 成功後重讀清單與狀態（例如停用最後一條規則 → 未啟用）；重讀失敗不影響這次寫入的結果
        await fetchStatus({ quiet: true }).catch(() => {});
        return { ok: true, data: result?.data ?? null };
      } catch (e) {
        if (!needsReload(e)) return fail(e);
        const reloaded = await fetchStatus({ quiet: true }).catch(() => null);
        if (!reloaded && e?.errorCode === 'VERSION_CONFLICT') {
          const result = fail(e);
          return { ...result, error: { ...result.error, message: STALE_VERSION_TEXT } };
        }
        // 重讀到的最新資料一併交回：呼叫端可以判斷操作的規則是否已在別處刪除
        return { ...fail(e), reloaded };
      }
    },
    [fetchStatus],
  );

  return {
    data,
    syncing,
    loadError,
    loadErrorDetail,
    // 處理中（等 202 結果或 MyAgent 正在套用）：「＋」、儲存、恢復通知、開關都先鎖住；輪詢逾時後解鎖
    busy: (awaiting || serverPending) && !pollTimedOut,
    pollTimedOut,
    reportedErrorCode,
    clearReportedError,
    refresh,
    sync,
    write,
  };
}
